import { configApiRef } from '@backstage/frontend-plugin-api';
import type { UserListFilterKind } from '@backstage/plugin-catalog-react';

type ConfigApi = typeof configApiRef.T;

const SCOPE_EXTENSION_ID = 'catalog-filter:catalog/list';
const KIND_EXTENSION_ID = 'catalog-filter:catalog/kind';

/*
 * Upstream's own defaults, from `plugin-catalog/alpha/filters`. They live in
 * those extensions' `configSchema`s, which are applied when the tree is built,
 * so they are not in the raw config read below and have to be repeated here.
 */
const DEFAULT_SCOPE: UserListFilterKind = 'owned';
const DEFAULT_KIND = 'component';

const isScope = (value: unknown): value is UserListFilterKind =>
  value === 'all' || value === 'owned' || value === 'starred';

/**
 * `initialFilter` off one of upstream's filter extensions.
 *
 * Giving this page keys of its own would let the two catalog pages disagree
 * about where the catalog opens, and there is no supported channel for reading
 * a sibling's config: `page:catalog`'s `filters` input cannot be widened to
 * carry the values, since redeclaring an input the parent already has is a type
 * error. So they are read straight out of `app.extensions`, which is
 * `deepVisibility: frontend` and shaped by `readAppExtensionsConfig`.
 */
function readInitialFilter(config: ConfigApi, extensionId: string): unknown {
  const entries = config.getOptional('app.extensions');
  if (!Array.isArray(entries)) {
    return undefined;
  }

  // Later entries overwrite earlier ones, as they do when the app resolves them.
  let value: unknown;
  for (const entry of entries) {
    if (typeof entry !== 'object' || entry === null) {
      continue;
    }
    const declared = (entry as Record<string, unknown>)[extensionId] as
      | { config?: { initialFilter?: unknown } }
      | undefined;
    if (declared?.config?.initialFilter !== undefined) {
      value = declared.config.initialFilter;
    }
  }
  return value;
}

/** The All/Owned/Starred chip the page opens on. */
export function readInitialScope(config: ConfigApi): UserListFilterKind {
  const value = readInitialFilter(config, SCOPE_EXTENSION_ID);
  return isScope(value) ? value : DEFAULT_SCOPE;
}

/**
 * The kind tab the page opens on. An empty string means no kind filter - the
 * "All" tab here, and upstream's own way of saying it, since `EntityKindPicker`
 * writes no filter for a falsy kind.
 */
export function readInitialKind(config: ConfigApi): string | undefined {
  const value = readInitialFilter(config, KIND_EXTENSION_ID);
  const kind = typeof value === 'string' ? value : DEFAULT_KIND;
  return kind === '' ? undefined : kind;
}
